// Maps Aria's cumulative captured fields onto the existing WizardState, so the
// conversational path feeds the SAME submit + render pipeline as the form.
import type { WizardState, Wall, RoomMarker, LayoutType } from "../types";

export interface AriaCaptured {
  layout_type?: string;
  has_island?: boolean;
  room_length_mm?: number;
  room_width_mm?: number;
  ceiling_height_mm?: number;
  window_wall?: string;
  door_wall?: string;
  door_style?: string;
  door_colour?: string;
  worktop_material?: string;
  handle_style?: string;
  flooring_type?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  complete?: boolean;
  /** Server-side colour→catalogue mapping (preserves the hex anchor). */
  _colour?: { colourName?: string; hex?: string; doorRangeId?: string };
}

const WALL_ID: Record<string, string> = { north: "w-n", east: "w-e", south: "w-s", west: "w-w" };

// Build a rectangular room (clockwise from top-left) + window/door markers on the
// named walls — gives the render engine real geometry from a rough conversation.
function synthRoom(lengthMm: number, widthMm: number, windowWall?: string, doorWall?: string): {
  walls: Wall[]; windows: RoomMarker[]; doors: RoomMarker[];
} {
  const W = Math.max(1500, Math.round(widthMm || 3000));
  const D = Math.max(1500, Math.round(lengthMm || 3500));
  const walls: Wall[] = [
    { id: "w-n", x1: 0, y1: 0, x2: W, y2: 0 },
    { id: "w-e", x1: W, y1: 0, x2: W, y2: D },
    { id: "w-s", x1: W, y1: D, x2: 0, y2: D },
    { id: "w-w", x1: 0, y1: D, x2: 0, y2: 0 },
  ];
  const winWall = WALL_ID[String(windowWall || "north").toLowerCase()] || "w-n";
  let doorWallId = WALL_ID[String(doorWall || "south").toLowerCase()] || "w-s";
  if (doorWallId === winWall) doorWallId = winWall === "w-n" ? "w-s" : "w-n";
  return {
    walls,
    windows: [{ id: "win-1", kind: "window", wallId: winWall, t: 0.5, widthMm: 1200 }],
    doors: [{ id: "dr-1", kind: "door", wallId: doorWallId, t: 0.5, widthMm: 800 }],
  };
}

const isLayout = (v?: string): v is LayoutType =>
  v === "single_wall" || v === "galley" || v === "l_shape" || v === "u_shape";

/** Merge captured fields into a copy of the wizard state. */
export function mapAriaToState(captured: AriaCaptured, base: WizardState): WizardState {
  const s: WizardState = JSON.parse(JSON.stringify(base)) as WizardState;

  if (captured.room_length_mm || captured.room_width_mm || captured.window_wall || captured.door_wall) {
    const { walls, windows, doors } = synthRoom(
      captured.room_length_mm || 0, captured.room_width_mm || 0, captured.window_wall, captured.door_wall,
    );
    s.room.walls = walls; s.room.windows = windows; s.room.doors = doors; s.room.boilers = [];
  }
  if (typeof captured.ceiling_height_mm === "number") s.room.ceilingHeightMm = captured.ceiling_height_mm;
  if (isLayout(captured.layout_type)) s.room.layoutType = captured.layout_type;
  if (typeof captured.has_island === "boolean") s.room.hasIsland = captured.has_island;

  // Colour → catalogue (hex anchor preserved); else the customer's own words.
  if (captured._colour?.colourName) s.style.colourName = captured._colour.colourName;
  else if (captured.door_colour) s.style.colourName = captured.door_colour;
  if (captured._colour?.doorRangeId) s.style.doorRangeId = captured._colour.doorRangeId;

  const set = (v: string | undefined): string | undefined => (v && v !== "unknown" ? v : undefined);
  const wm = set(captured.worktop_material); if (wm) s.style.worktopMaterial = wm;
  // Aria still emits the LEGACY composite token (its extraction schema in
  // kitchenos-api/modules/wizard_ai.js enumerates brass_bar/chrome_bar/…), so
  // split it back into the shape+finish pair the form now works in. Without
  // this, a lead captured by Aria would land on step 2 with nothing selected
  // in the Handles grid, because "brass_bar" is no longer one of its values.
  const hs = set(captured.handle_style);
  if (hs) {
    const [style, finish] = splitLegacyHandle(hs);
    s.style.handleStyle = style;
    if (finish) s.style.handleFinish = finish;
  }
  const ft = set(captured.flooring_type); if (ft) s.style.flooringType = ft;
  const ds = set(captured.door_style); if (ds) s.style.doorStyle = ds;

  if (captured.first_name) s.customer.firstName = captured.first_name;
  if (captured.last_name) s.customer.lastName = captured.last_name;
  if (captured.email) s.customer.email = captured.email;
  if (captured.phone) s.customer.phone = captured.phone;
  return s;
}

/** Inverse of `legacyHandleToken()` in submit.ts: legacy composite → the
 *  [shape, finish] pair the wizard form works in. Kept tolerant on purpose —
 *  Aria, older saved leads and the customer portal's render editor can all hand
 *  us a token from the pre-split vocabulary, and an unrecognised one should
 *  degrade to "shape only", never throw or blank the answer. */
export function splitLegacyHandle(token: string): [string, string] {
  const BAR: Record<string, string> = {
    brass_bar: "brass", chrome_bar: "chrome", black_bar: "black", nickel_bar: "steel",
  };
  const finish = BAR[token];
  if (finish) return ["bar", finish];
  return [token, ""];
}

export const validEmail = (e?: string): boolean =>
  !!e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

export const validUkMobile = (p?: string): boolean => {
  if (!p) return false;
  return /^(\+44|0)7\d{9}$/.test(p.replace(/[\s()-]/g, ""));
};

export const ariaReadyToSubmit = (c: AriaCaptured): boolean =>
  c.complete === true && validEmail(c.email) && validUkMobile(c.phone) && !!c.first_name;
